/**
 * LANDINGPAGE.JS v16.1 - MOBILE HAMBURGER MENU FIX
 *
 * Angepasst an Planivio Logo-Farbschema:
 * - Teal Primary: #4A90A4
 * - Soft Green: #6BA893
 * - Teal Light Background: #E8F4F8
 * - 12 Sections
 * - Responsive (Mobile-First)
 * - Mobile Hamburger Menu
 *
 * @version 16.1.0
 *
 * CHANGELOG:
 * v16.1.0 (2026-01-22): Fix Mobile Menu Button visibility
 * v16.0.0 (2026-01-22): + Mobile Hamburger Menu hinzugefügt
 * v15.0.0: Logo-Farben angepasst
 */

import { useState } from 'react';
import { usePublicPageView, trackPublicPageView } from '../hooks/useAnalytics';
import LanguageSwitcher from '../Components/common/LanguageSwitcher';
import { useTranslation } from '../context/I18nContext';

function LandingPage({ onGetStarted, onNavigate }) {
  usePublicPageView('landing');
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        :root {
          --primary: #4A90A4;
          --primary-light: #6BA893;
          --primary-dark: #3A7A8E;
          --dark: #0F172A;
          --dark-light: #1E293B;
          --gray-900: #111827;
          --gray-700: #374151;
          --gray-500: #6B7280;
          --gray-300: #D1D5DB;
          --gray-100: #F3F4F6;
          --white: #FFFFFF;
          --green: #10B981;
          --green-light: #D1FAE5;
          --red: #EF4444;
          --teal-bg: #E8F4F8;
          --teal-bg-mid: #D4E8EC;
          --teal-bg-dark: #B8D4DC;
          --gradient: linear-gradient(135deg, #4A90A4 0%, #6BA893 100%);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: var(--gray-700);
          overflow-x: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--gradient);
          color: white;
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(74, 144, 164, 0.4);
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(74, 144, 164, 0.5);
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--white);
          color: var(--gray-700);
          padding: 14px 28px;
          border: 1px solid var(--gray-300);
          border-radius: 12px;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .btn-secondary:hover {
          background: var(--gray-100);
          border-color: var(--gray-500);
        }

        /* ========== 1. BETA BANNER ========== */
        .beta-banner {
          background: var(--gradient);
          color: white;
          padding: 12px 24px;
          text-align: center;
          font-size: 0.9rem;
        }

        .beta-badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.25);
          color: white;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 700;
          margin-right: 8px;
          text-transform: uppercase;
        }

        /* ========== 2. NAVIGATION ========== */
        .nav {
          position: sticky;
          top: 0;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--gray-100);
          z-index: 1000;
          padding: 16px 0;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          text-decoration: none;
        }

        .nav-logo img {
          height: 36px;
          width: auto;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .nav-link {
          background: none;
          border: none;
          color: var(--gray-700);
          padding: 8px 14px;
          border-radius: 8px;
          font-family: inherit;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .nav-link:hover {
          background: var(--teal-bg);
          color: var(--primary);
        }

        .nav-link-highlight {
          background: linear-gradient(135deg, #F59E0B 0%, #F97316 100%);
          color: white !important;
          border-radius: 8px;
          font-weight: 600;
        }

        .nav-link-highlight:hover {
          background: linear-gradient(135deg, #F97316 0%, #EA580C 100%);
          color: white !important;
        }

        .nav-link-app {
          background: var(--primary) !important;
          color: white !important;
          border-radius: 8px;
          font-weight: 600;
        }

        .nav-link-app:hover {
          background: var(--primary-dark, #3a7a8a) !important;
          color: white !important;
        }

        /* Mobile Menu Button - NUR auf Mobile sichtbar */
        .mobile-menu-btn {
          display: none;
          align-items: center;
          justify-content: center;
          background: #FF6B35;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 24px;
          width: 48px;
          height: 48px;
          padding: 0;
          font-weight: bold;
          cursor: pointer;
          flex-shrink: 0;
          transition: all 0.2s ease;
        }

        .mobile-menu-btn:hover {
          background: #E55A2B;
          transform: scale(1.05);
        }

        /* Mobile: Hamburger Button anzeigen */
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex !important;
          }
        }

        /* Mobile Menu Overlay */
        .mobile-menu-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }

        .mobile-menu-overlay.open {
          display: block;
        }

        /* Mobile Menu Panel */
        .mobile-menu {
          position: fixed;
          top: 0;
          right: 0;
          width: 280px;
          height: 100vh;
          height: 100dvh; /* Dynamic viewport height for mobile */
          background: var(--white);
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          padding: 80px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          transform: translateX(100%);
          transition: transform 0.3s ease;
          visibility: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .mobile-menu.open {
          transform: translateX(0);
          visibility: visible;
        }

        /* Hide mobile menu on desktop */
        @media (min-width: 769px) {
          .mobile-menu,
          .mobile-menu-overlay {
            display: none !important;
          }
        }

        .mobile-menu-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--gray-700);
        }

        .mobile-menu-link {
          display: block;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          color: var(--gray-700);
          padding: 14px 16px;
          border-radius: 8px;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mobile-menu-link:hover {
          background: var(--teal-bg);
          color: var(--primary);
        }

        .mobile-menu-link.highlight {
          background: linear-gradient(135deg, #F59E0B 0%, #F97316 100%);
          color: white;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .mobile-menu-cta {
          margin-top: auto;
          width: 100%;
        }

        /* ========== 3. HERO SECTION ========== */
        .hero {
          background: linear-gradient(180deg, var(--teal-bg) 0%, var(--white) 100%);
          padding: 80px 0 100px;
          text-align: center;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--white);
          border: 1px solid var(--gray-300);
          padding: 8px 16px;
          border-radius: 100px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 24px;
          animation: fadeInUp 0.6s ease-out;
        }

        .hero-badge-dot {
          width: 8px;
          height: 8px;
          background: var(--green);
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .hero h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          line-height: 1.1;
          color: var(--dark);
          margin-bottom: 24px;
          animation: fadeInUp 0.6s ease-out 0.1s both;
        }

        .text-gradient {
          background: var(--gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: var(--gray-500);
          max-width: 650px;
          margin: 0 auto 40px;
          line-height: 1.6;
          animation: fadeInUp 0.6s ease-out 0.2s both;
        }

        .hero-cta {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 48px;
          animation: fadeInUp 0.6s ease-out 0.3s both;
        }

        .hero-trust {
          display: flex;
          gap: 32px;
          justify-content: center;
          flex-wrap: wrap;
          animation: fadeInUp 0.6s ease-out 0.4s both;
        }

        .hero-trust-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: var(--gray-500);
        }

        /* ========== 4. STATS SECTION ========== */
        .stats {
          background: var(--gradient);
          padding: 60px 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          text-align: center;
        }

        .stat-item {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 16px;
          padding: 32px 20px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(10px);
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--white);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.5;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stats-source {
          text-align: center;
          margin-top: 32px;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.85);
          font-style: italic;
        }

        /* ========== 5. FEATURES SECTION ========== */
        .features {
          background: var(--white);
          padding: 100px 0;
        }

        .section-label {
          display: inline-block;
          background: var(--teal-bg);
          color: var(--primary);
          padding: 6px 16px;
          border-radius: 100px;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-header h2 {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--dark);
          margin-bottom: 16px;
        }

        .section-header p {
          font-size: 1.1rem;
          color: var(--gray-500);
          max-width: 600px;
          margin: 0 auto;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }

        .feature-card {
          background: var(--teal-bg);
          border-radius: 24px;
          padding: 40px;
          transition: all 0.3s ease;
          border-top: 4px solid transparent;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(74, 144, 164, 0.15);
          border-top-color: var(--primary);
        }

        .feature-icon {
          font-size: 2.5rem;
          margin-bottom: 20px;
        }

        .feature-card h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--dark);
          margin-bottom: 12px;
        }

        .feature-card p {
          font-size: 0.95rem;
          color: var(--gray-500);
          line-height: 1.6;
        }

        /* ========== 6. COMPARISON SECTION ========== */
        .comparison {
          background: var(--teal-bg-mid);
          padding: 100px 0;
        }

        .comparison-cards {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .comparison-card {
          background: var(--white);
          border-radius: 20px;
          padding: 32px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .comparison-before,
        .comparison-after {
          padding: 24px;
          border-radius: 12px;
        }

        .comparison-before {
          background: #FEF2F2;
          border-left: 4px solid var(--red);
        }

        .comparison-after {
          background: #F0FDF4;
          border-left: 4px solid var(--green);
        }

        .comparison-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }

        .comparison-before .comparison-label {
          color: var(--red);
        }

        .comparison-after .comparison-label {
          color: var(--green);
        }

        .comparison-text {
          font-size: 0.95rem;
          color: var(--gray-700);
          line-height: 1.6;
        }

        /* ========== 7. PRICE COMPARISON SECTION ========== */
        .price-section {
          background: linear-gradient(135deg, var(--teal-bg) 0%, var(--teal-bg-dark) 100%);
          padding: 100px 0;
        }

        .coming-soon-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--primary);
          color: white;
          padding: 8px 20px;
          border-radius: 100px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .price-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }

        .price-item {
          background: var(--white);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .price-item.best {
          border: 2px solid var(--primary);
          box-shadow: 0 8px 24px rgba(74, 144, 164, 0.25);
        }

        .price-market {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 8px;
        }

        .price-value {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--dark);
          margin-bottom: 4px;
        }

        .price-diff {
          font-size: 0.85rem;
          color: var(--gray-500);
        }

        .price-diff.best {
          color: var(--primary);
          font-weight: 600;
        }

        .price-disclaimer {
          text-align: center;
          font-size: 0.85rem;
          color: var(--gray-500);
          margin-bottom: 24px;
        }

        .price-source {
          background: var(--white);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
        }

        .price-source p {
          font-size: 0.95rem;
          color: var(--gray-700);
          margin-bottom: 12px;
        }

        .price-source small {
          font-size: 0.8rem;
          color: var(--gray-500);
        }

        /* ========== 8. BENEFITS SECTION ========== */
        .benefits {
          background: var(--white);
          padding: 100px 0;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .benefit-card {
          text-align: center;
          padding: 32px 24px;
        }

        .benefit-icon-box {
          width: 72px;
          height: 72px;
          background: var(--teal-bg);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin: 0 auto 20px;
        }

        .benefit-card h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--dark);
          margin-bottom: 12px;
        }

        .benefit-card p {
          font-size: 0.9rem;
          color: var(--gray-500);
          line-height: 1.6;
        }

        /* ========== 9. TRUST/DSGVO SECTION ========== */
        .trust {
          background: var(--primary);
          padding: 100px 0;
        }

        .trust-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .trust-content h2 {
          font-size: clamp(1.75rem, 3vw, 2.25rem);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 20px;
          letter-spacing: -0.02em;
        }

        .trust-content p {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          margin-bottom: 16px;
        }

        .trust-content .highlight {
          color: var(--white);
          font-weight: 600;
        }

        .trust-box {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 24px;
          padding: 40px;
          backdrop-filter: blur(10px);
        }

        .trust-box-header {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 24px;
        }

        .trust-list {
          list-style: none;
        }

        .trust-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.95rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .trust-list li:last-child {
          border-bottom: none;
        }

        .trust-check {
          color: var(--white);
          font-weight: 700;
        }

        /* ========== 10. BETA INVITE SECTION ========== */
        .beta-invite {
          background: var(--teal-bg);
          padding: 100px 0;
          text-align: center;
        }

        .beta-benefits {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-top: 48px;
        }

        .beta-benefit-card {
          background: var(--white);
          border-radius: 20px;
          padding: 40px 32px;
          text-align: center;
        }

        .beta-benefit-icon {
          font-size: 2.5rem;
          margin-bottom: 20px;
        }

        .beta-benefit-card h3 {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--dark);
          margin-bottom: 12px;
        }

        .beta-benefit-card p {
          font-size: 0.9rem;
          color: var(--gray-500);
          line-height: 1.6;
        }

        /* ========== 11. FINAL CTA SECTION ========== */
        .final-cta {
          background: var(--gradient);
          padding: 120px 0;
          text-align: center;
        }

        .final-cta h2 {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .final-cta-subtitle {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 40px;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        .btn-white {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--white);
          color: var(--primary);
          padding: 16px 32px;
          border: none;
          border-radius: 12px;
          font-family: inherit;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
          text-decoration: none;
          margin-bottom: 32px;
        }

        .btn-white:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
        }

        .final-trust {
          display: flex;
          gap: 24px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .final-trust-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.85);
        }

        /* ========== 12. FOOTER ========== */
        .footer {
          background: var(--teal-bg-dark);
          padding: 48px 0;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 10;
        }

        .footer-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 24px;
        }

        .footer-logo img {
          height: 32px;
          width: auto;
        }

        .footer-links {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .footer-link {
          color: var(--gray-700);
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.2s;
          cursor: pointer;
          background: none;
          border: none;
          font-family: inherit;
        }

        .footer-link:hover {
          color: var(--primary);
        }

        .footer-copyright {
          color: var(--gray-500);
          font-size: 0.85rem;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .trust-grid {
            grid-template-columns: 1fr;
          }

          .price-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .nav-links {
            display: none !important;
          }

          .nav-inner {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            width: 100% !important;
          }

          .nav-logo {
            flex-shrink: 0;
          }

          .nav-logo img {
            height: 32px !important;
          }

          .hero {
            padding: 60px 0 80px;
          }

          .hero h1 {
            font-size: 2.2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 24px;
          }

          .stat-number {
            font-size: 2rem;
          }

          .benefits-grid {
            grid-template-columns: 1fr;
          }

          .beta-benefits {
            grid-template-columns: 1fr;
          }

          .comparison-card {
            grid-template-columns: 1fr;
          }

          .price-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .footer-inner {
            flex-direction: column;
            text-align: center;
          }

          .features,
          .comparison,
          .price-section,
          .benefits,
          .trust,
          .beta-invite {
            padding: 60px 0;
          }

          .final-cta {
            padding: 80px 0;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 12px !important;
          }

          .nav-logo img {
            height: 28px !important;
          }

          .hero-cta {
            flex-direction: column;
          }

          .hero-cta .btn-primary,
          .hero-cta .btn-secondary {
            width: 100%;
            justify-content: center;
          }

          .price-grid {
            grid-template-columns: 1fr;
          }

          .hero-trust {
            flex-direction: column;
            gap: 16px;
          }

          .final-trust {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>

      {/* 1. BETA BANNER */}
      <div className="beta-banner">
        <span className="beta-badge">BETA</span>
        {t('landing.betaBanner.text')}
      </div>

      {/* 2. NAVIGATION */}
      <nav className="nav">
        <div className="container nav-inner">
          <a href="#" className="nav-logo"><img src="/images/Logos/planivio_logo_primary_400px.png" alt="Planivio" /></a>
          <div className="nav-links">
            <button className="nav-link nav-link-highlight" onClick={() => { trackPublicPageView('nav_erlebe_planivio'); onNavigate && onNavigate('features'); }}>{"✨ "}{t('landing.betaInvite.title')}</button>
            <button className="nav-link" onClick={() => { trackPublicPageView('nav_features'); scrollToSection('features'); }}>{t('landing.nav.features')}</button>
            <button className="nav-link" onClick={() => { trackPublicPageView('nav_preise'); scrollToSection('preise'); }}>{t('landing.nav.pricing')}</button>
            <button className="nav-link" onClick={() => { trackPublicPageView('nav_datenschutz'); scrollToSection('datenschutz'); }}>{t('landing.nav.privacy')}</button>
            <button className="nav-link nav-link-app" onClick={() => { trackPublicPageView('cta_nav_app'); onGetStarted(); }}>{t('landing.nav.login')}</button>
            <LanguageSwitcher variant="dropdown" showLabel={false} />
          </div>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Menü öffnen"
          >☰</button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Menü schließen">✕</button>
        <button className="mobile-menu-link highlight" onClick={() => { onNavigate && onNavigate('features'); setMobileMenuOpen(false); }}>
          {"✨ "}{t('landing.betaInvite.title')}
        </button>
        <button className="mobile-menu-link" onClick={() => scrollToSection('features')}>{t('landing.nav.features')}</button>
        <button className="mobile-menu-link" onClick={() => scrollToSection('preise')}>{t('landing.nav.pricing')}</button>
        <button className="mobile-menu-link" onClick={() => scrollToSection('datenschutz')}>{t('landing.nav.privacy')}</button>
        <button className="btn-primary mobile-menu-cta" onClick={() => { onGetStarted(); setMobileMenuOpen(false); }}>{t('landing.nav.login')}</button>
        <div style={{ padding: '15px 20px', borderTop: '1px solid #e5e7eb' }}>
          <LanguageSwitcher variant="inline" showLabel={true} />
        </div>
      </div>

      {/* 3. HERO SECTION */}
      <section className="hero">
        <div className="container hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            {t('landing.hero.betaBadge')}
          </div>

          <h1>
            {t('landing.hero.titlePart1')}<br />
            <span className="text-gradient">{t('landing.hero.titleGradient')}</span>
          </h1>

          <p className="hero-subtitle">
            {t('landing.hero.lead')}{" "}{t('landing.hero.subtitle')}
          </p>

          <div className="hero-cta">
            <button className="btn-primary" onClick={() => { trackPublicPageView('cta_hero_start'); onGetStarted(); }}>
              {t('landing.hero.ctaStart')} →
            </button>
            <button className="btn-secondary" onClick={() => { trackPublicPageView('cta_hero_features'); onNavigate && onNavigate('features'); }}>
              {t('landing.hero.ctaFeatures')} →
            </button>
          </div>

          <div className="hero-trust">
            <span className="hero-trust-item">{"🇩🇪 "}{t('landing.trust.germany')}</span>
            <span className="hero-trust-item">{"🇪🇺 "}{t('landing.trust.eu')}</span>
            <span className="hero-trust-item">{"🔒 "}{t('landing.trust.dsgvo')}</span>
          </div>
        </div>
      </section>

      {/* 4. STATS SECTION */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">5-7</div>
              <div className="stat-label">{t('landing.stats.apps')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">{t('landing.stats.oneApp')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">&gt;100%</div>
              <div className="stat-label">{t('landing.stats.priceDiff')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">🇪🇺</div>
              <div className="stat-label">{t('landing.stats.euServer')}</div>
            </div>
          </div>
          <p className="stats-source">{t('landing.stats.source')}</p>
        </div>
      </section>

      {/* 5. FEATURES SECTION */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-label" style={{ background: '#F59E0B', color: 'white' }}>{t('landing.nav.features')}</span>
            <h2>{t('landing.features.title')}</h2>
            <p>{t('landing.features.subtitle')}</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🛒</div>
              <h3>{t('landing.features.shopping.title')}</h3>
              <p>{t('landing.features.shopping.desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>{t('landing.features.calendar.title')}</h3>
              <p>{t('landing.features.calendar.desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🍳</div>
              <h3>{t('landing.features.recipes.title')}</h3>
              <p>{t('landing.features.recipes.desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>{t('landing.features.notes.title')}</h3>
              <p>{t('landing.features.notes.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. COMPARISON SECTION */}
      <section className="comparison">
        <div className="container">
          <div className="section-header">
            <span className="section-label" style={{ background: '#F59E0B', color: 'white' }}>{t('landing.scenarios.before')} vs. {t('landing.scenarios.after')}</span>
            <h2>{t('landing.scenarios.title')}</h2>
            <p>{t('landing.scenarios.subtitle')}</p>
          </div>

          <div className="comparison-cards">
            <div className="comparison-card">
              <div className="comparison-before">
                <div className="comparison-label">{"❌ "}{t('landing.scenarios.before').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.shopping.before')}</p>
              </div>
              <div className="comparison-after">
                <div className="comparison-label">{"✓ "}{t('landing.scenarios.after').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.shopping.after')}</p>
              </div>
            </div>

            <div className="comparison-card">
              <div className="comparison-before">
                <div className="comparison-label">{"❌ "}{t('landing.scenarios.before').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.guests.before')}</p>
              </div>
              <div className="comparison-after">
                <div className="comparison-label">{"✓ "}{t('landing.scenarios.after').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.guests.after')}</p>
              </div>
            </div>

            <div className="comparison-card">
              <div className="comparison-before">
                <div className="comparison-label">{"❌ "}{t('landing.scenarios.before').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.birthday.before')}</p>
              </div>
              <div className="comparison-after">
                <div className="comparison-label">{"✓ "}{t('landing.scenarios.after').toUpperCase()}</div>
                <p className="comparison-text">{t('landing.scenarios.birthday.after')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PRICE COMPARISON SECTION */}
      <section className="price-section" id="preise">
        <div className="container">
          <div className="section-header">
            <span className="coming-soon-badge" style={{ background: '#F59E0B', color: 'white' }}>{"🔜 " + t('landing.priceCompare.comingSoon')}</span>
            <h2>{t('landing.priceCompare.title')}</h2>
            <p>{t('landing.priceCompare.subtitle')}</p>
          </div>

          <div className="price-grid">
            <div className="price-item best">
              <div className="price-market">Discounter A</div>
              <div className="price-value">87,40 €</div>
              <div className="price-diff best">{"✓ " + t('landing.priceCompare.cheapest')}</div>
            </div>
            <div className="price-item">
              <div className="price-market">Discounter B</div>
              <div className="price-value">91,20 €</div>
              <div className="price-diff">+3,80 €</div>
            </div>
            <div className="price-item">
              <div className="price-market">Verbrauchermarkt</div>
              <div className="price-value">98,50 €</div>
              <div className="price-diff">+11,10 €</div>
            </div>
            <div className="price-item">
              <div className="price-market">Vollsortimenter</div>
              <div className="price-value">112,30 €</div>
              <div className="price-diff">+24,90 €</div>
            </div>
            <div className="price-item">
              <div className="price-market">Premium-Markt</div>
              <div className="price-value">118,70 €</div>
              <div className="price-diff">+31,30 €</div>
            </div>
          </div>

          <p className="price-disclaimer">
            {t('landing.priceCompare.disclaimer')}
          </p>

          <div className="price-source">
            <p>{t('landing.priceCompare.sourceText')}</p>
            <small>{t('landing.priceCompare.sourceLabel')}</small>
          </div>
        </div>
      </section>

      {/* 8. BENEFITS SECTION */}
      <section className="benefits">
        <div className="container">
          <div className="section-header">
            <span className="section-label" style={{ background: '#F59E0B', color: 'white' }}>{t('landing.benefitsLabel')}</span>
            <h2>{t('landing.benefits.title')}</h2>
            <p>{t('landing.benefits.subtitle')}</p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon-box">🧘</div>
              <h3>{t('landing.benefits.peace.title')}</h3>
              <p>{t('landing.benefits.peace.text')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-box">👨‍👩‍👧‍👦</div>
              <h3>{t('landing.benefits.family.title')}</h3>
              <p>{t('landing.benefits.family.text')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-box">🤝</div>
              <h3>{t('landing.benefits.time.title')}</h3>
              <p>{t('landing.benefits.time.text')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-box">💰</div>
              <h3>{t('landing.benefits.save.title')}</h3>
              <p>{t('landing.benefits.save.text')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-box">😌</div>
              <h3>{t('landing.benefits.stress.title')}</h3>
              <p>{t('landing.benefits.stress.text')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon-box">🌟</div>
              <h3>{t('landing.benefits.focus.title')}</h3>
              <p>{t('landing.benefits.focus.text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. TRUST/DSGVO SECTION */}
      <section className="trust" id="datenschutz">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-content">
              <h2>{t('landing.privacy.title')}</h2>
              <p>{t('landing.privacy.text')}</p>
              <p className="highlight">{t('landing.privacy.highlight')}</p>
            </div>

            <div className="trust-box">
              <div className="trust-box-header">
                {"🇪🇺 "}{t('landing.privacy.promise')}
              </div>
              <ul className="trust-list">
                <li><span className="trust-check">✓</span> {t('landing.privacy.euServers')}</li>
                <li><span className="trust-check">✓</span> {t('landing.privacy.noThirdParty')}</li>
                <li><span className="trust-check">✓</span> {t('landing.privacy.noAds')}</li>
                <li><span className="trust-check">✓</span> {t('landing.privacy.encryption')}</li>
                <li><span className="trust-check">✓</span> {t('landing.privacy.deletable')}</li>
                <li><span className="trust-check">✓</span> {t('landing.privacy.transparent')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 10. BETA INVITE SECTION */}
      <section className="beta-invite">
        <div className="container">
          <div className="section-header">
            <h2>{t('landing.betaInvite.title')}</h2>
            <p>{t('landing.betaInvite.text')}</p>
          </div>

          <div className="beta-benefits">
            <div className="beta-benefit-card">
              <div className="beta-benefit-icon">🎁</div>
              <h3>{t('landing.betaPerks.free.title')}</h3>
              <p>{t('landing.betaPerks.free.desc')}</p>
            </div>
            <div className="beta-benefit-card">
              <div className="beta-benefit-icon">💬</div>
              <h3>{t('landing.betaPerks.feedback.title')}</h3>
              <p>{t('landing.betaPerks.feedback.desc')}</p>
            </div>
            <div className="beta-benefit-card">
              <div className="beta-benefit-icon">🚀</div>
              <h3>{t('landing.betaPerks.earlyAdopter.title')}</h3>
              <p>{t('landing.betaPerks.earlyAdopter.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA SECTION */}
      <section className="final-cta">
        <div className="container">
          <h2>{t('landing.cta.title')}</h2>
          <p className="final-cta-subtitle">
            {t('landing.cta.subtitle')}
          </p>
          <button className="btn-white" onClick={() => { trackPublicPageView('cta_bottom_start'); onGetStarted(); }}>
            {t('landing.cta.startButton')} →
          </button>
          <div className="final-trust">
            <span className="final-trust-item">{"🇩🇪 "}{t('landing.trust.germany')}</span>
            <span className="final-trust-item">{"🇪🇺 "}{t('landing.trust.eu')}</span>
            <span className="final-trust-item">{"🔒 "}{t('landing.trust.dsgvo')}</span>
            <span className="final-trust-item">{"💳 " + t('landing.trust.noCreditCard')}</span>
          </div>
        </div>
      </section>

      {/* Footer Spacer */}
      <div style={{ height: 120 }} />

      {/* 12. FOOTER */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-logo"><img src="/images/Logos/planivio_logo_primary_400px.png" alt="Planivio" /></div>
          <div className="footer-links">
            <button className="footer-link" onClick={() => onNavigate && onNavigate('impressum')}>{t('landing.footer.imprint')}</button>
            <button className="footer-link" onClick={() => onNavigate && onNavigate('datenschutz')}>{t('landing.footer.privacy')}</button>
            <button className="footer-link" onClick={() => onNavigate && onNavigate('agb')}>{t('landing.footer.terms')}</button>
            <a href="mailto:kontakt@planivio.de" className="footer-link">{t('landing.footer.contact')}</a>
          </div>
          <div className="footer-copyright">© 2026 PLANIVIO</div>
        </div>
      </footer>
    </>
  );
}

export default LandingPage;
